// Helpers shared by the Report-to-Support flow: session id, environment,
// module derivation from route, and a stable client-side fingerprint.

const SESSION_KEY = "vitolier_session_id";

export function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    window.sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export type AppEnvironment = "production" | "preview" | "development" | "staging" | "unknown";

export function detectEnvironment(): AppEnvironment {
  if (typeof window === "undefined") return "unknown";
  const host = window.location.hostname;
  if (host === "vitolier.com" || host === "www.vitolier.com" || host === "vitolier.app" || host === "www.vitolier.app") {
    return "production";
  }
  if (host.endsWith(".lovable.app") || host.endsWith(".lovable.dev")) return "preview";
  if (host === "localhost" || host === "127.0.0.1") return "development";
  const mode = (import.meta as { env?: Record<string, string> }).env?.MODE;
  if (mode === "development") return "development";
  if (mode === "production") return "staging";
  return "unknown";
}

// Map first meaningful URL segment (after optional locale) to a module name.
const MODULE_MAP: Record<string, string> = {
  app: "app", admin: "admin", trade: "trade", "cigar-atlas": "atlas",
  discover: "discover", events: "events", clubs: "clubs", support: "support",
  humidor: "humidor", brands: "brands", cigars: "cigars", retailers: "retailers",
  lounges: "lounges", culture: "culture",
};
const LOCALE_RX = /^[a-z]{2}(-[A-Z]{2})?$/;

export function deriveModule(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  let i = 0;
  if (parts[i] && LOCALE_RX.test(parts[i])) i++;
  const first = parts[i];
  if (!first) return "home";
  // /app/humidor -> humidor, /app/support -> support, else "app"
  if (first === "app" && parts[i + 1]) return MODULE_MAP[parts[i + 1]] ?? parts[i + 1];
  return MODULE_MAP[first] ?? first;
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
}

function normaliseMessage(msg: string): string {
  return msg
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "<uuid>")
    .replace(/\b\d{4,}\b/g, "<n>")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 300);
}

function firstStackFrame(stack?: string): string {
  if (!stack) return "";
  const lines = stack.split("\n").map(l => l.trim()).filter(Boolean);
  // Skip the first line which is usually the message itself.
  return (lines.find(l => l.startsWith("at ")) ?? lines[1] ?? "").slice(0, 200);
}

export async function computeFingerprint(opts: {
  errorName?: string;
  errorMessage: string;
  errorStack?: string;
  module: string;
}): Promise<string> {
  const seed = [
    opts.errorName ?? "Error",
    normaliseMessage(opts.errorMessage),
    firstStackFrame(opts.errorStack),
    opts.module,
  ].join("|");
  return sha256Hex(seed);
}
